import type {
  ApplicantSummary,
  StipulationListItem,
  StipulationListResponse,
} from "@credit-acceptance/step6-bff-client";

import { toStatusDisplay } from "./statusMap";
import type {
  StipulationActionView,
  StipulationApplicantType,
  StipulationHistoryView,
  StipulationListView,
  StipulationView,
} from "./types";

// Maps the BFF's raw `applicantType` codes to the panel's applicant literal.
// Unknown/absent codes return undefined rather than throwing, so a stip that
// isn't applicant-scoped (or an unrecognized future value) just stays
// unfiltered by applicant everywhere it's used.
const APPLICANT_TYPE: Record<string, StipulationApplicantType> = {
  DRIVER: "driver",
  SECOND_SIGNER: "secondSigner",
};

/**
 * Maps a raw BFF `applicantType` code to the panel's applicant literal.
 * @param {string} [raw] - The raw `StipulationListItem.applicantType` value.
 * @returns {StipulationApplicantType | undefined} The mapped applicant, or undefined when unknown.
 */
function mapApplicantType(raw: string | undefined): StipulationApplicantType | undefined {
  if (!raw) {
    return undefined;
  }
  return APPLICANT_TYPE[raw];
}

/**
 * Resolves the persisted POR-exception state for a stip's applicant by matching
 * `applicantPk` against `applicants.driver`/`.secondSigner` from the list response.
 * @param {number} [applicantPk] - The stip item's applicant primary key.
 * @param {ApplicantSummary} [applicants] - The list response's applicant summary.
 * @returns {boolean | undefined} The persisted `porExceptionInd`, or undefined when unmatched.
 */
function resolvePorExceptionInd(
  applicantPk: number | undefined,
  applicants: ApplicantSummary | undefined
): boolean | undefined {
  if (applicantPk === undefined || !applicants) {
    return undefined;
  }
  if (applicants.driver?.applicantPk === applicantPk) {
    return applicants.driver.porExceptionInd;
  }
  if (applicants.secondSigner?.applicantPk === applicantPk) {
    return applicants.secondSigner.porExceptionInd;
  }
  return undefined;
}

/**
 * Maps a generated `AvailableAction` to the action view, keeping only the fields
 * the action menu needs and normalizing the optional `reasons` submenu.
 * @param {NonNullable<StipulationListItem["availableActions"]>[number]} action - Generated action.
 * @returns {StipulationActionView} The panel-ready action.
 */
export function mapAvailableAction(
  action: NonNullable<StipulationListItem["availableActions"]>[number]
): StipulationActionView {
  return {
    actionCode: action.actionCode ?? "",
    displayName: action.displayName ?? action.actionCode ?? "",
    requiresNote: action.requiresNote ?? false,
    reasons: (action.reasons ?? []).map((reason) => ({
      reasonPk: reason.reasonPk,
      displayName: reason.displayName ?? "",
    })),
  };
}

/**
 * Maps a generated `ActionHistoryEntry` to the history view.
 * @param {NonNullable<StipulationListItem["history"]>[number]} entry - Generated history entry.
 * @returns {StipulationHistoryView} The panel-ready history entry.
 */
function mapHistory(entry: NonNullable<StipulationListItem["history"]>[number]): StipulationHistoryView {
  return {
    actionName: entry.actionName,
    reasonDescription: entry.reasonDescription,
    noteText: entry.noteText,
    performedBy: entry.performedBy,
    performedAt: entry.performedAt,
  };
}

/**
 * Maps a single generated `StipulationListItem` to the panel view. The BFF item
 * has no `id`; compose one from `stipCode` + `applicantPk` so Accordion keys stay
 * unique when the same stip applies to multiple applicants. When both are absent
 * the composite is empty, so fall back to the array index to keep keys unique.
 * `title` falls back to `stipCode` when the server omits it.
 * @param {StipulationListItem} item - Generated item.
 * @param {number} index - Position in the list, used as an id fallback.
 * @returns {StipulationView} The panel-ready stipulation.
 */
export function mapStipulationItem(item: StipulationListItem, index: number): StipulationView {
  const stipCode = item.stipCode ?? "";
  const composite = `${stipCode}:${item.applicantPk ?? ""}`;
  const stipulationAppliedId = item.stipulationAppliedId;
  return {
    id: composite === ":" ? `stip-${index}` : composite,
    stipCode,
    stipulationAppliedId,
    title: item.title ?? stipCode,
    status: item.status ?? "",
    statusDisplay: toStatusDisplay(item.status),
    categoryCode: item.categoryCode,
    description: item.description,
    policyTag: item.policyTag,
    currentAction: item.currentAction,
    openLoanStip: item.openLoanStip,
    applicantPk: item.applicantPk,
    applicantType: mapApplicantType(item.applicantType),
    workbenchScreens: item.workbenchScreens,
    availableActions: (item.availableActions ?? []).map((action) => mapAvailableAction(action)),
    recentNote: item.recentNote
      ? {
          actionName: item.recentNote.actionName,
          description: item.recentNote.description,
          auditInformation: item.recentNote.auditInformation,
        }
      : undefined,
    history: (item.history ?? []).map((entry) => mapHistory(entry)),
    secondaryProofsAllowed: item.secondaryProofsAllowed,
    isBankVerificationAllowed: item.isBankVerificationAllowed,
    isRedFlag: item.isRedFlag,
  };
}

/**
 * Maps the generated `StipulationListResponse` to the panel view model. Counts
 * come from the response (defaulting to 0) rather than being recomputed, since
 * the server may have filtered the item set via `openOnly`.
 * @param {StipulationListResponse} response - The BFF response body.
 * @returns {StipulationListView} The panel-ready list.
 */
export function mapStipulationList(response: StipulationListResponse): StipulationListView {
  return {
    items: (response.stipulations ?? []).map((item, index) => ({
      ...mapStipulationItem(item, index),
      porExceptionInd: resolvePorExceptionInd(item.applicantPk, response.applicants),
    })),
    openCount: response.openCount ?? 0,
    totalCount: response.totalCount ?? 0,
    hasSecondSigner: Boolean(response.applicants?.secondSigner),
  };
}
